import { DetailRequirement, TraitId } from '@cibo/core'
import { GeometryEditor, GeometryEditorContext } from '@cibo/mapgl'
import { useWindowTitle } from '@cibo/profile'
import { THEME_DARK } from '@cibo/ui'
import Close from '@mui/icons-material/Close'
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Hidden,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material'
import { FeatureCollection, featureCollection, union } from '@turf/turf'
import { useSnackbar } from 'notistack'
import { ascend, pick, propEq, propOr, sort, uniqBy } from 'ramda'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  FieldEditorCard,
  FieldLabelEditor,
  NameEditorForm,
  TraitTable,
  useField,
  useSaveDetail,
} from '../../.'

const EDITABLE_TRAITS: TraitId[] = [
  'geometry',
  'nativeVegetationConversionDate',
  'includesWetlandDesignation',
  'coverCrop',
  'cashCrop',
  'biomassBurning',
  'tillage',
  'livestock',
  'fertilizer',
]

type EditLandProps = {
  editLabel?: boolean
  traitRequirements: DetailRequirement[]
}

export const EditLand = ({ editLabel, traitRequirements }: EditLandProps) => {
  const { t } = useTranslation('@cibo/landmanager/EditLand')
  const { resourceId } = useParams()
  const saveDetail = useSaveDetail()
  const navigate = useNavigate()
  const { data: fieldModel, isFetched, isLoading, error } = useField(resourceId)
  const { enqueueSnackbar } = useSnackbar()
  const [focusTraitId, setFocusTraitId] = useState<TraitId>('geometry')

  useWindowTitle(t('windowTitle', { name: fieldModel?.name }))

  const initialFeatures = useMemo(
    () => (fieldModel?.feature ? featureCollection([fieldModel.feature]) : featureCollection([])),
    [fieldModel?.feature]
  )

  if ((!isFetched && !error) || (isLoading && !fieldModel)) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  const handleSaveGeometry = (featureCollection: FeatureCollection) => {
    if (resourceId) {
      try {
        //@ts-ignore
        const combinedFeature = featureCollection.features.reduce(union)

        saveDetail.mutateAsync(
          {
            resourceId,
            details: [
              {
                traitId: 'geometry',
                input: {
                  feature: combinedFeature,
                },
              },
            ],
          },
          {
            onSuccess: () => {
              enqueueSnackbar(t('geometrySave', { context: 'onSuccess' }), { variant: 'success' })
            },
            onError: () => {
              enqueueSnackbar(
                `${t('geometrySave', { context: 'onError' })}: ${JSON.stringify(
                  // @ts-ignore saveDetail is not appropriately typed
                  saveDetail.error?.message
                )}`,
                {
                  variant: 'error',
                }
              )
            },
          }
        )
      } catch (err) {
        console.log(err)
      }
    }
  }

  const availableEditableTraits = EDITABLE_TRAITS.filter(traitId =>
    traitRequirements.some(propEq('traitId', traitId))
  )

  const traitDetails = traitRequirements.filter(propEq('traitId', focusTraitId)) || []

  // uniqBy (below) picks the first unique detail - so put "FULL" details first
  const fullDetailsFirst = sort(ascend(propOr('false', 'resultsOnly')), traitDetails)

  const editorDetails = uniqBy(pick(['traitId', 'year']), fullDetailsFirst)

  return (
    <>
      <Grid sx={{ flex: 1, height: '100%', overflow: 'hidden' }} container>
        <Hidden smDown>
          <Grid sm={0} md={4} lg={2} sx={{ height: '100%', overflowY: 'auto' }} item>
            <ThemeProvider theme={THEME_DARK}>
              <Paper sx={{ minHeight: '100%', borderRadius: 0 }}>
                <Stack direction="row" justifyContent="space-between" p={1}>
                  <IconButton onClick={() => navigate(-1)} title={t('back')}>
                    <Close />
                  </IconButton>
                </Stack>
                <Divider />
                <Stack spacing={2} p={2}>
                  <Stack>
                    {!!fieldModel?.locationLabel && (
                      <Typography>{fieldModel.locationLabel}</Typography>
                    )}
                    {!!fieldModel && <NameEditorForm field={fieldModel} />}
                  </Stack>
                  {!!editLabel && !!fieldModel && <FieldLabelEditor field={fieldModel} />}
                </Stack>
                <List component="nav" aria-label="Field Details">
                  {availableEditableTraits.map(traitId => (
                    <ListItemButton
                      key={traitId}
                      selected={focusTraitId === traitId}
                      onClick={() => setFocusTraitId(traitId)}
                    >
                      <ListItemText primary={t('traitListItem', { context: traitId })} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </ThemeProvider>
          </Grid>
        </Hidden>
        <Grid sm={12} md={8} lg={10} sx={{ height: '100%', display: 'flex' }} item>
          <Box sx={{ flex: 1, display: 'flex' }}>
            {focusTraitId === 'geometry' ? (
              <GeometryEditorContext resourceId={resourceId} initialFeatures={initialFeatures}>
                <GeometryEditor disabled={!fieldModel?.can(['edit_land'])}>
                  {/* @ts-ignore these are untyped */}
                  {({ features, touched }) =>
                    fieldModel && (
                      <FieldEditorCard
                        features={features}
                        loading={saveDetail.isLoading}
                        onSave={handleSaveGeometry}
                        touched={touched}
                      />
                    )
                  }
                </GeometryEditor>
              </GeometryEditorContext>
            ) : (
              !!fieldModel && (
                <Box sx={{ flex: 1 }} p={3}>
                  <TraitTable
                    // @ts-ignore
                    detailRequirements={editorDetails}
                    fieldModels={[fieldModel]}
                    traitId={focusTraitId}
                  />
                </Box>
              )
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  )
}
